import styled from "@emotion/styled";
import { Link, NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { getTheme } from "../../redux/themeSlice";

interface Theme {
  palette: {
    [key: string]: {
      main: string;
      contrastText: string;
      outline?: string;
      hover: string;
      hoverText?: string;
      disabled?: {
        main: string;
      };
    };
  };
  typography: {
    fontFamily: string;
  };
}

interface VariantProps {
  variant: Variant;
  color: string;
  theme: Theme;
}

type Variant =
  | "outline"
  | "solid"
  | "plain"
  | "listItem"
  | "dark"
  | "tab"
  | "chip"
  | "hero"
  | "link";

const getPropsByVariant = ({ variant, color, theme }: VariantProps) => {
  const palette: any = theme.palette[color] ?? theme.palette.primary;

  const variants: Record<Variant, { main: any; hover: any }> = {
    outline: {
      main: {
        border: `1px solid ${palette.outline}`,
        color: palette.outline,
      },
      hover: {
        border: `1px solid ${palette.hover}`,
        backgroundColor: palette.hover,
        color: palette.hoverText,
        // color: palette.hover,
      },
    },
    solid: {
      main: {
        border: `1px solid ${palette.main}`,
        backgroundColor: palette.main,
        color: palette.contrastText,
      },
      hover: {
        border: `1px solid ${palette.hover}`,
        backgroundColor: palette.hover,
        color: palette.hoverText,
      },
    },
    plain: {
      main: {
        border: "none",
        backgroundColor: "transparent",
        color: palette.hover || "#000",
      },
      hover: {
        border: "none",
        backgroundColor: "transparent",
        color: palette.hover,
      },
    },
    listItem: {
      main: {
        border: "none",
        backgroundColor: "transparent",
        color: palette.hover,
      },
      hover: {
        border: "none",
        backgroundColor: "#f0f0f0",
        textDecoration: "underline",
        color: palette.main,
      },
    },
    dark: {
      main: {
        border: "none",
        backgroundColor: "transparent",
        color: palette.contrastText,
      },
      hover: {
        border: "none",
        backgroundColor: "transparent",
        color: palette.main,
      },
    },
    tab: {
      main: {
        borderBottomColor: palette.main,
        backgroundColor: "transparent",
        color: "inherit",
      },
      hover: {
        borderBottomColor: palette.main,
        backgroundColor: "transparent",
        color: theme.palette.secondary.main,
      },
    },
    chip: {
      main: {
        backgroundColor: palette.main,
        color: palette.contrastText,
      },
      hover: {
        backgroundColor: palette.main,
      },
    },
    hero: {
      main: {
        border: "0px",
        backgroundColor: "none",
        color: theme.palette.secondary.main,
      },
      hover: {
        color: theme.palette.primary.main,
      },
    },
    link: {
      main: {
        border: "none",
        backgroundColor: "transparent",
        color: palette.main,
        textDecoration: "underline",
      },
      hover: {
        color: palette.hover,
      },
    },
  };

  return variants[variant] ?? variants.solid;
};

const getDisabledProps = (theme: Theme) => ({
  main: {
    border: `1px solid ${theme.palette.disabled?.main}`,
    backgroundColor: theme.palette.disabled?.main,
    color: theme.palette.primary.contrastText,
    pointerEvents: "none",
  },
  hover: {},
});

interface StyledButtonProps {
  color?: string;
  variant?: Variant;
  disabled?: boolean;
  theme?: Theme;
}

const StyledButton = ({
  color = "primary",
  variant = "solid",
  disabled = false,
  theme,
}: StyledButtonProps) => {
  if (!theme || Object.keys(theme).length === 0) {
    theme = useSelector(getTheme);
  }

  const propsByVariant = disabled
    ? getDisabledProps(theme)
    : getPropsByVariant({ variant, color, theme });

  return {
    fontWeight: 700,
    cursor: !disabled ? "pointer" : "default",
    transition: "all 0.3s linear",
    textDecoration: variant === "link" ? "underline" : "none",
    whiteSpace: variant !== "hero" ? "nowrap" : "normal",
    fontFamily: theme.typography.fontFamily,
    ...(propsByVariant && propsByVariant.main),
    "&:hover, &.active, &:active": !disabled && {
      ...(propsByVariant && propsByVariant.hover),
    },
  };
};

const IGNORED_PROPS: string[] = [];

const buttonConfig = {
  shouldForwardProp: (prop: string) => !IGNORED_PROPS.includes(prop),
};

export const Button = styled("button", buttonConfig)(StyledButton);
export const ButtonLink = styled(Link, buttonConfig)(StyledButton);
export const ButtonAnchor = styled("a", buttonConfig)(StyledButton);
export const ButtonNavLink = styled(NavLink, buttonConfig)(StyledButton);
export const HeroCardLink = styled(Link, buttonConfig)(StyledButton);
export const Chip = styled("span", buttonConfig)(StyledButton);
export const TabLink = styled("div", buttonConfig)(StyledButton);
